/**
 * SWATCHER REVIEW UTILITY
 * 
 * Helps identify and credit swatchers by grouping visually similar photos.
 * Uses perceptual hashing (dHash) to find photos with similar:
 * - Watermarks
 * - Hand positions
 * - Backgrounds
 * - Skin tones
 * 
 * Process:
 * 1. Load all polish items from Firebase
 * 2. Generate perceptual hash for each photo
 * 3. Group photos by hash similarity
 * 4. Review groups and assign swatcher credits
 * 5. Bulk update to Firebase
 * 
 * Route: /review-swatchers
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, writeBatch } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { 
  ArrowLeft, Play, Pause, RotateCcw, Check, ChevronDown, ChevronUp,
  Image, Users, Loader2, AlertCircle, Save,
  Sparkles, Camera
} from 'lucide-react';
import type { CollectionItem, Photo } from '../types';

// ============================================================================
// TYPES
// ============================================================================

interface PhotoWithMeta {
  url: string;
  hash: string;
  polishId: string;
  polishName: string;
  polishBrand: string;
  photoIndex: number;
  currentCredit?: string;
  currentCreditUrl?: string;
}

interface PhotoGroup {
  id: string;
  samplePhoto: PhotoWithMeta;
  photos: PhotoWithMeta[];
  suggestedCredit?: string;
  assignedCredit: string;
  assignedCreditUrl: string;
  isExpanded: boolean;
  isApplied: boolean;
}

interface AnalysisProgress {
  phase: 'idle' | 'loading' | 'hashing' | 'grouping' | 'complete';
  current: number;
  total: number;
  message: string;
}

// ============================================================================
// INDEXEDDB STORAGE (localStorage is too small for 15k+ photos)
// ============================================================================

const DB_NAME = 'swatcherReviewDB';
const DB_VERSION = 1;
const STORE_NAME = 'analysisData';

const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
};

const saveToIndexedDB = async (key: string, data: unknown): Promise<void> => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(data, key);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
      
      transaction.oncomplete = () => db.close();
    });
  } catch (e) {
    console.warn('IndexedDB save failed:', e);
  }
};

const loadFromIndexedDB = async <T,>(key: string): Promise<T | null> => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(key);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result || null);
      
      transaction.oncomplete = () => db.close();
    });
  } catch (e) {
    console.warn('IndexedDB load failed:', e);
    return null;
  }
};

const clearIndexedDB = async (): Promise<void> => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
      
      transaction.oncomplete = () => db.close();
    });
  } catch (e) {
    console.warn('IndexedDB clear failed:', e);
  }
};

// ============================================================================
// PERCEPTUAL HASHING UTILITIES
// ============================================================================

/**
 * Generate a difference hash (dHash) for an image
 * This creates a 64-bit hash based on relative brightness differences
 */
const generateDHash = async (imageUrl: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    
    const timeout = setTimeout(() => {
      reject(new Error('Image load timeout'));
    }, 10000);
    
    img.onload = () => {
      clearTimeout(timeout);
      try {
        // Create canvas and resize to 9x8 (we need 9 wide to compare 8 pairs)
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        canvas.width = 9;
        canvas.height = 8;
        
        // Draw and resize image
        ctx.drawImage(img, 0, 0, 9, 8);
        
        // Get pixel data
        const imageData = ctx.getImageData(0, 0, 9, 8);
        const pixels = imageData.data;
        
        // Convert to grayscale and compute hash
        let hash = '';
        for (let y = 0; y < 8; y++) {
          for (let x = 0; x < 8; x++) {
            const leftIdx = (y * 9 + x) * 4;
            const rightIdx = (y * 9 + x + 1) * 4;
            
            // Grayscale values
            const leftGray = pixels[leftIdx] * 0.299 + pixels[leftIdx + 1] * 0.587 + pixels[leftIdx + 2] * 0.114;
            const rightGray = pixels[rightIdx] * 0.299 + pixels[rightIdx + 1] * 0.587 + pixels[rightIdx + 2] * 0.114;
            
            // Compare left to right
            hash += leftGray > rightGray ? '1' : '0';
          }
        }
        
        // Convert binary string to hex for easier storage/comparison
        const hexHash = parseInt(hash, 2).toString(16).padStart(16, '0');
        resolve(hexHash);
      } catch (err) {
        reject(err);
      }
    };
    
    img.onerror = () => {
      clearTimeout(timeout);
      reject(new Error('Failed to load image'));
    };
    
    img.src = imageUrl;
  });
};

/**
 * Calculate Hamming distance between two hex hashes
 * Lower distance = more similar
 */
const hammingDistance = (hash1: string, hash2: string): number => {
  const bin1 = parseInt(hash1, 16).toString(2).padStart(64, '0');
  const bin2 = parseInt(hash2, 16).toString(2).padStart(64, '0');
  
  let distance = 0;
  for (let i = 0; i < 64; i++) {
    if (bin1[i] !== bin2[i]) distance++;
  }
  return distance;
};

/**
 * Group photos by similarity using their hashes
 * Threshold: photos with hamming distance <= threshold are considered similar
 * Now async to prevent UI freeze
 */
const groupPhotosByHash = async (
  photos: PhotoWithMeta[], 
  threshold: number = 10,
  onProgress?: (current: number, total: number) => void
): Promise<PhotoGroup[]> => {
  const groups: PhotoGroup[] = [];
  const assigned = new Set<string>();
  
  const chunkSize = 100; // Process 100 photos per chunk
  
  for (let i = 0; i < photos.length; i++) {
    const photo = photos[i];
    
    if (assigned.has(photo.url)) continue;
    
    // Find all similar photos
    const similar: PhotoWithMeta[] = [];
    
    for (const p of photos) {
      if (assigned.has(p.url)) continue;
      if (p.url === photo.url) {
        similar.push(p);
        continue;
      }
      if (hammingDistance(photo.hash, p.hash) <= threshold) {
        similar.push(p);
      }
    }
    
    if (similar.length >= 1) {
      // Mark all as assigned
      similar.forEach(p => assigned.add(p.url));
      
      // Check if any photo in the group already has credit
      const existingCredit = similar.find(p => p.currentCredit)?.currentCredit || '';
      const existingCreditUrl = similar.find(p => p.currentCreditUrl)?.currentCreditUrl || '';
      
      groups.push({
        id: `group-${groups.length}`,
        samplePhoto: photo,
        photos: similar,
        assignedCredit: existingCredit,
        assignedCreditUrl: existingCreditUrl,
        isExpanded: false,
        isApplied: false
      });
    }
    
    // Yield to UI every chunk
    if (i % chunkSize === 0) {
      if (onProgress) onProgress(i, photos.length);
      await new Promise(resolve => setTimeout(resolve, 10));
    }
  }
  
  // Sort by group size (largest first)
  return groups.sort((a, b) => b.photos.length - a.photos.length);
};

// ============================================================================
// COMPONENT
// ============================================================================

export default function SwatcherReview() {
  const navigate = useNavigate();
  
  // Auth state
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Analysis state
  const [progress, setProgress] = useState<AnalysisProgress>({
    phase: 'idle',
    current: 0,
    total: 0,
    message: 'Ready to start analysis'
  });
  const [isRunning, setIsRunning] = useState(false);
  const [photos, setPhotos] = useState<PhotoWithMeta[]>([]);
  const [groups, setGroups] = useState<PhotoGroup[]>([]);
  
  // Settings
  const [similarityThreshold, setSimilarityThreshold] = useState(10);
  const [minGroupSize, setMinGroupSize] = useState(2);
  const [photoSelection, setPhotoSelection] = useState<'first' | 'all'>('first');
  
  // UI state
  const [activeTab, setActiveTab] = useState<'analyze' | 'review'>('analyze');
  const [savingGroups, setSavingGroups] = useState<Set<string>>(new Set());
  const [savedCount, setSavedCount] = useState(0);
  
  // Refs for cancellation
  const cancelRef = useRef(false);
  
  // ============================================================================
  // AUTH
  // ============================================================================
  
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUserId(user?.uid || null);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);
  
  // ============================================================================
  // LOAD FROM INDEXEDDB
  // ============================================================================
  
  useEffect(() => {
    const loadSavedData = async () => {
      // Clean up old localStorage data (was causing quota errors)
      try {
        localStorage.removeItem('swatcherReview_photos');
        localStorage.removeItem('swatcherReview_groups');
      } catch (e) {
        // Ignore - just cleanup
      }
      
      try {
        const savedPhotos = await loadFromIndexedDB<PhotoWithMeta[]>('photos');
        const savedGroups = await loadFromIndexedDB<PhotoGroup[]>('groups');
        
        if (savedPhotos && savedGroups && savedPhotos.length > 0 && savedGroups.length > 0) {
          setPhotos(savedPhotos);
          setGroups(savedGroups);
          setProgress({
            phase: 'complete',
            current: savedGroups.length,
            total: savedPhotos.length,
            message: `Loaded previous analysis: ${savedGroups.length} groups from ${savedPhotos.length} photos`
          });
          setActiveTab('review');
        }
      } catch (e) {
        console.error('Error loading saved analysis:', e);
      }
    };
    
    loadSavedData();
  }, []);
  
  // ============================================================================
  // SAVE TO INDEXEDDB
  // ============================================================================
  
  useEffect(() => {
    if (photos.length > 0 && groups.length > 0) {
      saveToIndexedDB('photos', photos);
      saveToIndexedDB('groups', groups);
    }
  }, [photos, groups]);
  
  // ============================================================================
  // ANALYSIS FUNCTIONS
  // ============================================================================
  
  const startAnalysis = async () => {
    if (!currentUserId) return;
    
    cancelRef.current = false;
    setIsRunning(true);
    setPhotos([]);
    setGroups([]);
    
    try {
      // Phase 1: Load polish items
      setProgress({
        phase: 'loading',
        current: 0,
        total: 0,
        message: 'Loading polish items from database...'
      });
      
      const itemsRef = collection(db, 'users', currentUserId, 'items');
      const snapshot = await getDocs(itemsRef);
      
      const polishItems: CollectionItem[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.itemType === 'polish' && data.photos && data.photos.length > 0) {
          polishItems.push({
            id: docSnap.id,
            ...data
          } as CollectionItem);
        }
      });
      
      if (cancelRef.current) return;
      
      // Build list of photos based on selection
      const allPhotos: { item: CollectionItem; photo: Photo; index: number }[] = [];
      polishItems.forEach(item => {
        if (item.photos && item.photos.length > 0) {
          if (photoSelection === 'first') {
            // Get first/default photo only (typically the swatch)
            const defaultPhoto = item.photos.find(p => p.isDefault) || item.photos[0];
            const index = item.photos.findIndex(p => p.url === defaultPhoto.url);
            allPhotos.push({ item, photo: defaultPhoto, index: index >= 0 ? index : 0 });
          } else {
            // Get all photos
            item.photos.forEach((photo, index) => {
              allPhotos.push({ item, photo, index });
            });
          }
        }
      });
      
      const photoTypeLabel = photoSelection === 'first' ? 'swatch photos' : 'photos';
      setProgress({
        phase: 'hashing',
        current: 0,
        total: allPhotos.length,
        message: `Analyzing ${allPhotos.length} ${photoTypeLabel} from ${polishItems.length} polishes...`
      });
      
      // Phase 2: Generate hashes
      const processedPhotos: PhotoWithMeta[] = [];
      const batchSize = 20; // Process 20 at a time
      
      for (let i = 0; i < allPhotos.length; i += batchSize) {
        if (cancelRef.current) return;
        
        const batch = allPhotos.slice(i, i + batchSize);
        const results = await Promise.allSettled(
          batch.map(async ({ item, photo, index }) => {
            try {
              const hash = await generateDHash(photo.url);
              return {
                url: photo.url,
                hash,
                polishId: item.id,
                polishName: item.name || 'Unknown',
                polishBrand: item.brand || 'Unknown',
                photoIndex: index,
                currentCredit: photo.creditName,
                currentCreditUrl: photo.creditUrl
              } as PhotoWithMeta;
            } catch (err) {
              console.warn(`Failed to hash photo: ${photo.url}`, err);
              return null;
            }
          })
        );
        
        results.forEach(result => {
          if (result.status === 'fulfilled' && result.value) {
            processedPhotos.push(result.value);
          }
        });
        
        setProgress({
          phase: 'hashing',
          current: Math.min(i + batchSize, allPhotos.length),
          total: allPhotos.length,
          message: `Analyzing photos... ${Math.min(i + batchSize, allPhotos.length)} / ${allPhotos.length}`
        });
        
        // Small delay to prevent UI freezing
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      
      if (cancelRef.current) return;
      
      // Phase 3: Group by similarity (async to prevent freeze)
      setProgress({
        phase: 'grouping',
        current: 0,
        total: processedPhotos.length,
        message: 'Grouping similar photos (this may take a few minutes)...'
      });
      
      // Small delay to let the UI update
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const photoGroups = await groupPhotosByHash(
        processedPhotos, 
        similarityThreshold,
        (current, total) => {
          setProgress({
            phase: 'grouping',
            current,
            total,
            message: `Grouping similar photos... ${current} / ${total}`
          });
        }
      );
      
      if (cancelRef.current) return;
      
      // Filter by minimum group size
      const filteredGroups = photoGroups.filter(g => g.photos.length >= minGroupSize);
      
      setPhotos(processedPhotos);
      setGroups(filteredGroups);
      
      setProgress({
        phase: 'complete',
        current: filteredGroups.length,
        total: processedPhotos.length,
        message: `Found ${filteredGroups.length} groups from ${processedPhotos.length} photos`
      });
      
      setActiveTab('review');
      
    } catch (error) {
      console.error('Analysis error:', error);
      setProgress({
        phase: 'idle',
        current: 0,
        total: 0,
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsRunning(false);
    }
  };
  
  const cancelAnalysis = () => {
    cancelRef.current = true;
    setIsRunning(false);
    setProgress(prev => ({
      ...prev,
      message: 'Analysis cancelled'
    }));
  };
  
  const resetAnalysis = async () => {
    setPhotos([]);
    setGroups([]);
    setSavedCount(0);
    await clearIndexedDB();
    setProgress({
      phase: 'idle',
      current: 0,
      total: 0,
      message: 'Ready to start analysis'
    });
    setActiveTab('analyze');
  };
  
  // ============================================================================
  // GROUP MANAGEMENT
  // ============================================================================
  
  const toggleGroupExpanded = (groupId: string) => {
    setGroups(prev => prev.map(g => 
      g.id === groupId ? { ...g, isExpanded: !g.isExpanded } : g
    ));
  };
  
  const updateGroupCredit = (groupId: string, field: 'assignedCredit' | 'assignedCreditUrl', value: string) => {
    setGroups(prev => prev.map(g => 
      g.id === groupId ? { ...g, [field]: value } : g
    ));
  };
  
  const applyGroupCredit = async (group: PhotoGroup) => {
    if (!currentUserId || !group.assignedCredit.trim()) return;
    
    setSavingGroups(prev => new Set(prev).add(group.id));
    
    try {
      // Group photos by polishId for batch updates
      const photosByPolish = new Map<string, PhotoWithMeta[]>();
      group.photos.forEach(photo => {
        const existing = photosByPolish.get(photo.polishId) || [];
        existing.push(photo);
        photosByPolish.set(photo.polishId, existing);
      });
      
      // Update each polish document
      const batch = writeBatch(db);
      
      for (const [polishId, polishPhotos] of photosByPolish) {
        const polishRef = doc(db, 'users', currentUserId, 'items', polishId);
        
        // Get current document to update photos array
        const docSnap = await getDocs(collection(db, 'users', currentUserId, 'items'));
        const polishDoc = docSnap.docs.find(d => d.id === polishId);
        
        if (polishDoc) {
          const currentPhotos = polishDoc.data().photos || [];
          const updatedPhotos = currentPhotos.map((photo: Photo, idx: number) => {
            const matchingPhoto = polishPhotos.find(p => p.photoIndex === idx);
            if (matchingPhoto) {
              return {
                ...photo,
                creditName: group.assignedCredit,
                creditUrl: group.assignedCreditUrl || photo.creditUrl
              };
            }
            return photo;
          });
          
          batch.update(polishRef, { photos: updatedPhotos });
        }
      }
      
      await batch.commit();
      
      // Mark group as applied
      setGroups(prev => prev.map(g => 
        g.id === group.id ? { ...g, isApplied: true } : g
      ));
      
      setSavedCount(prev => prev + 1);
      
    } catch (error) {
      console.error('Error applying credit:', error);
      alert('Failed to save credits. Please try again.');
    } finally {
      setSavingGroups(prev => {
        const newSet = new Set(prev);
        newSet.delete(group.id);
        return newSet;
      });
    }
  };
  
  // ============================================================================
  // RENDER
  // ============================================================================
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-fuchsia-500" />
      </div>
    );
  }
  
  if (!currentUserId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="glass-card p-8 text-center">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-glass mb-2">Sign In Required</h2>
          <p className="text-glass-secondary">Please sign in to use this utility.</p>
        </div>
      </div>
    );
  }
  
  const progressPercent = progress.total > 0 
    ? Math.round((progress.current / progress.total) * 100) 
    : 0;
  
  const uncreditedGroups = groups.filter(g => !g.isApplied);
  const creditedGroups = groups.filter(g => g.isApplied);
  
  return (
    <div className="min-h-screen pb-12">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-700/50 rounded-lg transition-default"
          >
            <ArrowLeft className="icon-base text-glass-secondary" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-glass font-heading flex items-center gap-3">
              <Camera className="w-8 h-8 text-fuchsia-500" />
              Swatcher Review
            </h1>
            <p className="text-glass-secondary font-body">
              Group similar photos to identify and credit swatchers
            </p>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('analyze')}
            className={`px-6 py-3 rounded-xl font-bold transition-all ${
              activeTab === 'analyze'
                ? 'bg-fuchsia-500 text-white'
                : 'bg-slate-700/50 text-glass-secondary hover:bg-slate-600/50'
            }`}
          >
            <Sparkles className="icon-sm inline mr-2" />
            Analyze
          </button>
          <button
            onClick={() => setActiveTab('review')}
            disabled={groups.length === 0}
            className={`px-6 py-3 rounded-xl font-bold transition-all ${
              activeTab === 'review'
                ? 'bg-fuchsia-500 text-white'
                : 'bg-slate-700/50 text-glass-secondary hover:bg-slate-600/50'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <Users className="icon-sm inline mr-2" />
            Review ({groups.length} groups)
          </button>
        </div>
        
        {/* Analyze Tab */}
        {activeTab === 'analyze' && (
          <div className="space-y-6">
            {/* Settings Card */}
            <div className="glass-card p-6">
              <h2 className="text-lg font-bold text-glass mb-4 flex items-center gap-2">
                <Image className="w-5 h-5 text-fuchsia-400" />
                Analysis Settings
              </h2>
              
              {/* Photo Selection - Full Width */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-glass-secondary mb-2">
                  Photos to Analyze
                </label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setPhotoSelection('first')}
                    disabled={isRunning}
                    className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all ${
                      photoSelection === 'first'
                        ? 'bg-fuchsia-500 text-white'
                        : 'bg-slate-700/50 text-glass-secondary hover:bg-slate-600/50'
                    } disabled:opacity-50`}
                  >
                    <div className="font-bold">First/Swatch Photo Only</div>
                    <div className="text-xs opacity-80 mt-1">
                      Compare one photo per polish (~5,000 photos)
                    </div>
                  </button>
                  <button
                    onClick={() => setPhotoSelection('all')}
                    disabled={isRunning}
                    className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all ${
                      photoSelection === 'all'
                        ? 'bg-fuchsia-500 text-white'
                        : 'bg-slate-700/50 text-glass-secondary hover:bg-slate-600/50'
                    } disabled:opacity-50`}
                  >
                    <div className="font-bold">All Photos</div>
                    <div className="text-xs opacity-80 mt-1">
                      Compare every photo (~15,000 photos)
                    </div>
                  </button>
                </div>
                <p className="text-sm text-glass-muted mt-2">
                  💡 <strong>Recommended:</strong> Use "First/Swatch Photo Only" to group by swatcher. 
                  Each polish typically has photos from one swatcher.
                  Or use the <a href="/review-swatchers-swatch-only" className="text-fuchsia-400 underline hover:text-fuchsia-300">dedicated Swatch-Only page</a> with separate storage.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-glass-secondary mb-2">
                    Similarity Threshold
                    <span className="text-glass-muted ml-2">(lower = stricter matching)</span>
                  </label>
                  <input
                    type="range"
                    min="5"
                    max="20"
                    value={similarityThreshold}
                    onChange={(e) => setSimilarityThreshold(Number(e.target.value))}
                    disabled={isRunning}
                    className="w-full accent-fuchsia-500"
                  />
                  <div className="flex justify-between text-sm text-glass-muted mt-1">
                    <span>Strict (5)</span>
                    <span className="font-bold text-fuchsia-400">{similarityThreshold}</span>
                    <span>Loose (20)</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-glass-secondary mb-2">
                    Minimum Group Size
                  </label>
                  <input
                    type="number"
                    min="2"
                    max="20"
                    value={minGroupSize}
                    onChange={(e) => setMinGroupSize(Number(e.target.value))}
                    disabled={isRunning}
                    className="w-full px-4 py-2 bg-slate-800/60 border border-slate-600 rounded-lg text-glass"
                  />
                  <p className="text-sm text-glass-muted mt-1">
                    Only show groups with at least this many photos
                  </p>
                </div>
              </div>
            </div>
            
            {/* Progress Card */}
            <div className="glass-card p-6">
              <h2 className="text-lg font-bold text-glass mb-4">
                {progress.phase === 'idle' ? 'Ready to Analyze' :
                 progress.phase === 'complete' ? 'Analysis Complete' :
                 'Analyzing...'}
              </h2>
              
              {/* Progress Bar */}
              {progress.phase !== 'idle' && (
                <div className="mb-4">
                  <div className="h-4 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-fuchsia-500 to-purple-500 transition-all duration-300"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-2 text-sm">
                    <span className="text-glass-secondary">{progress.message}</span>
                    <span className="text-fuchsia-400 font-bold">{progressPercent}%</span>
                  </div>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex flex-col gap-3">
                {progress.phase === 'complete' && groups.length > 0 && (
                  <div className="flex items-center gap-2 p-3 bg-amber-500/20 border border-amber-500/50 rounded-xl text-amber-200 text-sm">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <span>
                      Running a new analysis will replace your current {groups.length} groups. 
                      Make sure you've saved any credits you want to keep!
                    </span>
                  </div>
                )}
                <div className="flex gap-3">
                  {!isRunning ? (
                    <>
                      <button
                        onClick={startAnalysis}
                        className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-fuchsia-500 to-purple-500 text-white rounded-xl font-bold hover:from-fuchsia-600 hover:to-purple-600 transition-all"
                      >
                        <Play className="w-5 h-5" />
                        {progress.phase === 'complete' ? 'Re-analyze' : 'Start Analysis'}
                      </button>
                      {progress.phase === 'complete' && (
                        <button
                          onClick={resetAnalysis}
                          className="flex items-center gap-2 px-6 py-3 bg-slate-700 text-glass rounded-xl font-bold hover:bg-slate-600 transition-all"
                        >
                          <RotateCcw className="w-5 h-5" />
                          Reset
                        </button>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={cancelAnalysis}
                      className="flex items-center gap-2 px-6 py-3 bg-red-500/80 text-white rounded-xl font-bold hover:bg-red-600 transition-all"
                    >
                      <Pause className="w-5 h-5" />
                      Cancel
                    </button>
                  )}
                </div>
              </div>
              
              {/* Stats */}
              {progress.phase === 'complete' && (
                <div className="mt-6 grid grid-cols-3 gap-4">
                  <div className="bg-slate-800/60 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-fuchsia-400">{photos.length}</p>
                    <p className="text-sm text-glass-muted">Photos Analyzed</p>
                  </div>
                  <div className="bg-slate-800/60 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-purple-400">{groups.length}</p>
                    <p className="text-sm text-glass-muted">Groups Found</p>
                  </div>
                  <div className="bg-slate-800/60 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-green-400">{savedCount}</p>
                    <p className="text-sm text-glass-muted">Groups Credited</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Review Tab */}
        {activeTab === 'review' && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="glass-card p-4 flex items-center justify-between">
              <div className="text-glass-secondary">
                <span className="font-bold text-glass">{uncreditedGroups.length}</span> groups to review
                {creditedGroups.length > 0 && (
                  <span className="ml-4 text-green-400">
                    <Check className="w-4 h-4 inline mr-1" />
                    {creditedGroups.length} credited
                  </span>
                )}
              </div>
              <button
                onClick={resetAnalysis}
                className="text-sm text-glass-muted hover:text-glass"
              >
                <RotateCcw className="w-4 h-4 inline mr-1" />
                Reset
              </button>
            </div>
            
            {/* Groups */}
            {groups.map((group) => (
              <div 
                key={group.id}
                className={`glass-card overflow-hidden transition-all ${
                  group.isApplied ? 'border-2 border-green-500/50 bg-green-500/5' : ''
                }`}
              >
                {/* Group Header */}
                <div 
                  className="p-4 flex items-center gap-4 cursor-pointer hover:bg-slate-700/30"
                  onClick={() => toggleGroupExpanded(group.id)}
                >
                  {/* Sample Image */}
                  <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-slate-800">
                    <img
                      src={group.samplePhoto.url}
                      alt="Sample"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-1 bg-fuchsia-500/20 text-fuchsia-300 rounded-full text-sm font-bold">
                        {group.photos.length} photos
                      </span>
                      {group.isApplied && (
                        <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded-full text-sm font-bold">
                          <Check className="w-3 h-3 inline mr-1" />
                          Credited
                        </span>
                      )}
                      {group.photos[0].currentCredit && !group.isApplied && (
                        <span className="px-2 py-1 bg-amber-500/20 text-amber-300 rounded-full text-sm">
                          Has existing credit
                        </span>
                      )}
                    </div>
                    <p className="text-glass-secondary text-sm truncate">
                      Including: {group.photos.slice(0, 3).map(p => p.polishName).join(', ')}
                      {group.photos.length > 3 && ` +${group.photos.length - 3} more`}
                    </p>
                  </div>
                  
                  {/* Credit Input (collapsed view) */}
                  <div className="hidden md:flex items-center gap-2" onClick={e => e.stopPropagation()}>
                    <input
                      type="text"
                      placeholder="Swatcher name..."
                      value={group.assignedCredit}
                      onChange={(e) => updateGroupCredit(group.id, 'assignedCredit', e.target.value)}
                      className="w-40 px-3 py-2 bg-slate-800/80 border border-slate-600 rounded-lg text-glass text-sm"
                    />
                    <button
                      onClick={() => applyGroupCredit(group)}
                      disabled={!group.assignedCredit.trim() || savingGroups.has(group.id) || group.isApplied}
                      className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {savingGroups.has(group.id) ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : group.isApplied ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        <Save className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  
                  {/* Expand Arrow */}
                  <div className="text-glass-muted">
                    {group.isExpanded ? (
                      <ChevronUp className="w-6 h-6" />
                    ) : (
                      <ChevronDown className="w-6 h-6" />
                    )}
                  </div>
                </div>
                
                {/* Expanded Content */}
                {group.isExpanded && (
                  <div className="border-t border-slate-700 p-4">
                    {/* Mobile Credit Input */}
                    <div className="md:hidden mb-4 flex gap-2">
                      <input
                        type="text"
                        placeholder="Swatcher name..."
                        value={group.assignedCredit}
                        onChange={(e) => updateGroupCredit(group.id, 'assignedCredit', e.target.value)}
                        className="flex-1 px-3 py-2 bg-slate-800/80 border border-slate-600 rounded-lg text-glass"
                      />
                      <button
                        onClick={() => applyGroupCredit(group)}
                        disabled={!group.assignedCredit.trim() || savingGroups.has(group.id) || group.isApplied}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg font-bold"
                      >
                        {savingGroups.has(group.id) ? 'Saving...' : 'Apply'}
                      </button>
                    </div>
                    
                    {/* Credit URL Input */}
                    <div className="mb-4">
                      <label className="block text-sm text-glass-muted mb-1">
                        Credit URL (optional - Instagram, website, etc.)
                      </label>
                      <input
                        type="text"
                        placeholder="https://instagram.com/..."
                        value={group.assignedCreditUrl}
                        onChange={(e) => updateGroupCredit(group.id, 'assignedCreditUrl', e.target.value)}
                        className="w-full px-3 py-2 bg-slate-800/80 border border-slate-600 rounded-lg text-glass text-sm"
                      />
                    </div>
                    
                    {/* Photo Grid */}
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                      {group.photos.map((photo, idx) => (
                        <div key={idx} className="group relative">
                          <div className="aspect-square rounded-lg overflow-hidden bg-slate-800">
                            <img
                              src={photo.url}
                              alt={photo.polishName}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                          {/* Hover tooltip */}
                          <div className="absolute inset-0 bg-black/75 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-1 rounded-lg">
                            <p className="text-white text-xs text-center font-medium truncate w-full">
                              {photo.polishName}
                            </p>
                            <p className="text-gray-300 text-xs truncate w-full text-center">
                              {photo.polishBrand}
                            </p>
                            {photo.currentCredit && (
                              <p className="text-amber-300 text-xs mt-1">
                                Credit: {photo.currentCredit}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            
            {groups.length === 0 && (
              <div className="glass-card p-12 text-center">
                <Image className="w-16 h-16 text-glass-muted mx-auto mb-4" />
                <h3 className="text-xl font-bold text-glass mb-2">No Groups Found</h3>
                <p className="text-glass-secondary">
                  Run an analysis to find similar photos
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
